/**
 * The administrative API, typed at the edge of this package.
 *
 * These interfaces mirror the server's response models. They are hand-written
 * rather than pulled from the generated OpenAPI types, and that is a trade
 * worth naming: the generated types are the authority and CI fails on drift,
 * but reaching into `components["schemas"]["AdminUserPayload"]` at every call
 * site makes the code unreadable. So the shapes are named here, once, and a
 * contract change shows up as a compile error in this file rather than in
 * fifteen screens.
 */

import type { ApiClient } from "@hardauto/core-api";

export type UserStatus = "pending_verification" | "active" | "suspended" | "deactivated";

export type UserTier = "system" | "tenant_owner" | "tenant_user" | "guest";

export interface AdminUser {
  id: string;
  email: string | null;
  display_name: string | null;
  tier: UserTier;
  status: UserStatus;
  email_verified: boolean;
  locale: string | null;
  timezone: string | null;
  created_at: string | null;
}

/** A role as the listing names it. */
export interface RoleRef {
  key: string;
  name: string;
}

/** An account in the listing. `roles` is null when the caller may not read
 *  roles, which the screen tells apart from an account holding none. */
/**
 * What became of the invitation sent with a new account: `sent`; `skipped` (the box was
 * unticked); `disabled` (this installation sends none); `failed` (the account exists and
 * the invitation can be sent again); or one of `not_needed`, `no_address`, `unknown`.
 */
export type InvitationStatus =
  | "sent"
  | "skipped"
  | "disabled"
  | "failed"
  | "not_needed"
  | "no_address"
  | "unknown";

export interface CreatedUser extends AdminUser {
  invitation: InvitationStatus;
}

export interface AdminUserListItem extends AdminUser {
  roles?: RoleRef[] | null;
}

export interface UserPage {
  users: AdminUserListItem[];
  total: number;
  next_cursor: string | null;
}

export interface Role {
  id: string;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  permissions: string[];
  assigned_users: number | null;
}

export interface Permission {
  key: string;
  label: string;
  module: string;
  description: string;
}

export interface AuditEntry {
  action: string;
  resource_type: string | null;
  resource_id: string | null;
  actor_user_id: string | null;
  correlation_id: string | null;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  result: string;
  occurred_at: string;
}

export interface AuditPage {
  entries: AuditEntry[];
  cursor: string | null;
}

/** Filters a user listing accepts. */
export interface UserQuery {
  status?: UserStatus | undefined;
  search?: string | undefined;
  after_id?: string | undefined;
  limit?: number | undefined;
}

function query(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    // Empty strings are dropped, not sent. `?search=` would be a filter for
    // the empty string rather than "no filter", and the server would honour it.
    if (value === undefined || value === "") continue;
    search.set(key, String(value));
  }
  const encoded = search.toString();
  return encoded ? `?${encoded}` : "";
}

/** Every administrative call, in one place. */
export class AdminApi {
  readonly #client: ApiClient;

  constructor(client: ApiClient) {
    this.#client = client;
  }

  #path(tail: string): string {
    return `/api/v1/admin${tail}`;
  }

  listUsers(filters: UserQuery = {}): Promise<UserPage> {
    return this.#client.request<UserPage>(this.#path(`/users${query({ ...filters })}`));
  }

  getUser(id: string): Promise<AdminUser> {
    return this.#client.request<AdminUser>(this.#path(`/users/${id}`));
  }

  createUser(body: {
    email: string;
    display_name?: string | undefined;
    tier?: UserTier | undefined;
    send_invitation?: boolean | undefined;
  }): Promise<CreatedUser> {
    return this.#client.request<CreatedUser>(this.#path("/users"), {
      method: "POST",
      body,
    });
  }

  /** Send the invitation again to somebody who has not activated their account. */
  resendInvitation(id: string): Promise<{ invitation: InvitationStatus }> {
    return this.#client.request<{ invitation: InvitationStatus }>(
      this.#path(`/users/${id}/invitation`),
      { method: "POST" },
    );
  }

  updateUser(
    id: string,
    body: { display_name?: string; locale?: string; timezone?: string },
  ): Promise<AdminUser> {
    return this.#client.request<AdminUser>(this.#path(`/users/${id}`), {
      method: "PATCH",
      body,
    });
  }

  changeStatus(id: string, status: UserStatus, reason?: string): Promise<AdminUser> {
    return this.#client.request<AdminUser>(this.#path(`/users/${id}/status`), {
      method: "POST",
      body: { status, ...(reason ? { reason } : {}) },
    });
  }

  userRoles(id: string): Promise<{ roles: Role[] }> {
    return this.#client.request<{ roles: Role[] }>(this.#path(`/users/${id}/roles`));
  }

  assignRoles(id: string, roleKeys: string[]): Promise<{ roles: Role[] }> {
    return this.#client.request<{ roles: Role[] }>(this.#path(`/users/${id}/roles`), {
      // PUT, not PATCH: the body is the complete set the user should hold, so
      // two administrators editing concurrently store an intent someone
      // actually saw rather than a merge of two half-instructions.
      method: "PUT",
      body: { role_keys: roleKeys },
    });
  }

  listRoles(): Promise<{ roles: Role[] }> {
    return this.#client.request<{ roles: Role[] }>(this.#path("/roles"));
  }

  createRole(body: {
    key: string;
    name: string;
    description?: string;
    permissions: string[];
  }): Promise<Role> {
    return this.#client.request<Role>(this.#path("/roles"), { method: "POST", body });
  }

  updateRole(
    id: string,
    body: { name: string; description?: string; permissions: string[] },
  ): Promise<Role> {
    return this.#client.request<Role>(this.#path(`/roles/${id}`), {
      method: "PATCH",
      body,
    });
  }

  deleteRole(id: string): Promise<void> {
    return this.#client.request<void>(this.#path(`/roles/${id}`), { method: "DELETE" });
  }

  listPermissions(): Promise<{ permissions: Permission[] }> {
    return this.#client.request<{ permissions: Permission[] }>(this.#path("/permissions"));
  }

  readAudit(
    filters: {
      action?: string | undefined;
      resource_type?: string | undefined;
      before_id?: string | undefined;
      limit?: number | undefined;
    } = {},
  ): Promise<AuditPage> {
    return this.#client.request<AuditPage>(this.#path(`/audit${query({ ...filters })}`));
  }
}
